"use client";

import { useRef, useState } from "react";
import { Notice, StatCard, StatGrid } from "@/components/tool-kit/primitives";
import {
  BUTTON_PRIMARY,
  BUTTON_SECONDARY,
  HINT,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import NoiseBar from "@/components/tools/games-organizers/noise-bar";
import NoiseHistory from "@/components/tools/games-organizers/noise-history";
import NoiseSettings from "@/components/tools/games-organizers/noise-settings";
import {
  FULL_SCREEN_FRAME,
  useFullScreen,
} from "@/components/tools/games-organizers/use-full-screen";
import {
  type MeterStatus,
  useNoiseMeter,
} from "@/components/tools/games-organizers/use-noise-meter";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  DEFAULT_SETTINGS,
  type Settings,
  warning,
  type Zone,
  zoneShares,
} from "@/lib/tool-kit/games-organizers/noise-meter";

/** English strings. A translated content file overrides these through `ui`. */
export const CLASSROOM_NOISE_METER_STRINGS = {
  privacy:
    "Audio is analysed on this device as it arrives, only to measure how loud it is. It is never recorded, stored or sent anywhere, and the page keeps only the level numbers. The microphone is not used until you press Start listening.",
  relative:
    "Levels are relative, not calibrated decibels (dB SPL). They depend on your device's microphone, so use the sensitivity slider until ordinary working noise sits in the middle of the bar.",
  start: "Start listening",
  stop: "Stop listening",
  asking: "Waiting for your browser's permission to use the microphone…",
  resetSession: "Reset session",
  fullScreen: "Full screen",
  exitFullScreen: "Exit full screen",
  denied:
    "Microphone access was blocked. Allow it in your browser's site settings (usually the icon beside the address), then press Start listening again.",
  unsupported:
    "This browser cannot reach a microphone from this page. Use a current version of Chrome, Edge, Firefox or Safari over a secure (https) connection.",
  failed:
    "The microphone stopped or could not be started. Check that one is connected and not in use by another app, then press Start listening again.",
  heading: "Noise level",
  notListening: "Not listening",
  zones: { quiet: "Quiet", talk: "Talking", loud: "Too loud" },
  meterLabel: "Noise level, from silent to very loud",
  historyLabel: "Noise level over the last minute",
  defaultMessage: "Please lower your voices.",
  summaryHeading: "This session",
  sessionTime: "Time listened",
  share: "{value}% of the time",
  secondsValue: "{value} s",
  minutesSeconds: "{minutes}:{seconds}",
  settingsHeading: "Settings",
  sensitivityLabel: "Sensitivity ({value} dB)",
  sensitivityHint:
    "Raise it if the bar barely moves, lower it if a quiet room already looks busy.",
  quietLabel: "Quiet below {value}%",
  quietHint: "Under this point the bar is green.",
  loudLabel: "Too loud from {value}%",
  loudHint: "From this point the bar is red.",
  warnLabel: "Show a warning when it stays too loud for",
  warnHint: "Choose Off to turn the warning off.",
  warnOff: "Off",
  warnOption: "one:{count} second|other:{count} seconds",
  messageLabel: "Warning message",
  messagePlaceholder: "Please lower your voices.",
} as const;

const SETTINGS_START: Settings = DEFAULT_SETTINGS;
const DEFAULT_WARN = 5;

function statusNote(
  strings: typeof CLASSROOM_NOISE_METER_STRINGS,
  status: MeterStatus
): string | null {
  switch (status) {
    case "asking":
      return strings.asking;
    case "denied":
      return strings.denied;
    case "unsupported":
      return strings.unsupported;
    case "failed":
      return strings.failed;
    default:
      return null;
  }
}

export default function ClassroomNoiseMeterWidget() {
  const strings = useToolUi(CLASSROOM_NOISE_METER_STRINGS);
  const fmt = useToolNumber();
  const frame = useRef<HTMLDivElement>(null);
  const screen = useFullScreen(frame);
  const [settings, setSettings] = useState<Settings>(SETTINGS_START);
  const [warnAfter, setWarnAfter] = useState(DEFAULT_WARN);
  const [message, setMessage] = useState("");
  const { status, meter, start, stop, reset } = useNoiseMeter(settings);

  const listening = status === "listening";
  const note = statusNote(strings, status);
  const shares = zoneShares(meter.time);
  const total = meter.time.quiet + meter.time.talk + meter.time.loud;
  const seconds = Math.floor(total / 1000);
  const zoneName = (zone: Zone) => strings.zones[zone];
  const alarm = listening && warning(meter, warnAfter);

  return (
    <div
      className={`space-y-6 ${screen.wide ? FULL_SCREEN_FRAME : ""}`}
      ref={frame}
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className={WIDGET_H2}>{strings.heading}</h2>
        <div className="flex flex-wrap gap-2">
          <button
            className={BUTTON_PRIMARY}
            disabled={status === "asking"}
            onClick={listening ? stop : start}
            type="button"
          >
            {listening ? strings.stop : strings.start}
          </button>
          <button
            aria-pressed={screen.wide}
            className={BUTTON_SECONDARY}
            onClick={() => screen.set(!screen.wide)}
            type="button"
          >
            {screen.wide ? strings.exitFullScreen : strings.fullScreen}
          </button>
        </div>
      </div>

      <div aria-live="polite">{note ? <Notice>{note}</Notice> : null}</div>

      <p
        aria-live="polite"
        className={`text-center font-bold text-4xl sm:text-6xl ${listening ? "text-foreground" : "text-muted-foreground"}`}
      >
        {listening ? zoneName(meter.zone) : strings.notListening}
      </p>
      <NoiseBar
        label={strings.meterLabel}
        meter={meter}
        settings={settings}
        valueText={listening ? zoneName(meter.zone) : strings.notListening}
      />
      <div aria-live="polite" className="min-h-[3rem]">
        {alarm ? (
          <p className="rounded-lg border border-red-500 bg-red-500/10 p-3 text-center font-semibold text-foreground text-xl sm:text-3xl">
            {message.trim() === "" ? strings.defaultMessage : message}
          </p>
        ) : null}
      </div>
      <NoiseHistory
        history={meter.history}
        label={strings.historyLabel}
        settings={settings}
      />

      <div className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h3 className="font-semibold text-foreground">
            {strings.summaryHeading}
          </h3>
          <button className={BUTTON_SECONDARY} onClick={reset} type="button">
            {strings.resetSession}
          </button>
        </div>
        <StatGrid>
          {(["quiet", "talk", "loud"] as const).map((zone) => (
            <StatCard
              key={zone}
              label={`${zoneName(zone)} · ${t(strings.share, { value: fmt(shares[zone]) })}`}
              value={t(strings.secondsValue, {
                value: fmt(Math.round(meter.time[zone] / 1000)),
              })}
            />
          ))}
        </StatGrid>
        <p className={HINT}>
          {strings.sessionTime}:{" "}
          {t(strings.minutesSeconds, {
            minutes: fmt(Math.floor(seconds / 60)),
            seconds: fmt(seconds % 60, { minimumIntegerDigits: 2 }),
          })}
        </p>
      </div>

      {screen.wide ? null : (
        <>
          <NoiseSettings
            message={message}
            onMessage={setMessage}
            onSettings={setSettings}
            onWarnAfter={setWarnAfter}
            settings={settings}
            strings={strings}
            warnAfter={warnAfter}
          />
          <p className={HINT}>{strings.relative}</p>
        </>
      )}
      <p className={HINT}>{strings.privacy}</p>
    </div>
  );
}
